import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CalendarModule } from 'primeng/calendar';
import { MessageService, ConfirmationService } from 'primeng/api';

import { CasContact as Contact, CasContactService } from '../../services/cas-contact.service';
import { CasTransaction as Transaction, CasTransactionService } from '../../services/cas-transaction.service';
import { ProjectHistoryService } from '../../services/project-history.service';
import { RingoverService } from '../../../cac/services/ringover.service';
import { AuthService } from '../../../../shared/service/auth.service';
import { UserService } from '../../../../shared/service/user.service';
import { professionalCategories, origines as origineEnumOptions } from '../../../cac/models/enum';

export const CYCLE_DE_VIE_OPTIONS   = ['Lead', 'SQL', 'Opportunité', 'Client', 'Evangelist', 'Autre'];
// Statuts de lead — doivent correspondre EXACTEMENT aux valeurs du moteur de
// workflow backend (app/services/workflow_engine.py : STATUT_*). Certains sont
// posés automatiquement selon la phase (Nouveau, En cours - Devis Envoyé,
// En cours - QS Envoyé, Converti en transaction), les autres manuellement.
export const STATUT_LEAD_OPTIONS    = ['Nouveau', 'En cours - Devis Envoyé', 'A recontacter', 'NRP', 'Non qualifié - Refus', 'Perdu - Trop Cher', 'Perdu - Refus Assureur', 'Converti en transaction'];
export const TYPE_CONTACT_OPTIONS   = ['Prospect', 'Client', 'Partenaire', 'Fournisseur Lead', 'Collaborateur', 'Ancien Client'];
export const ETAT_CIVIL_OPTIONS     = ['Célibataire', 'Marié', 'Pacsé', 'Veuf', 'Concubinage'];
export const ORIGINE_LEAD_OPTIONS   = ['Lead Assureur', 'Lead Fournisseur', 'Appel Entrant', 'Parrainage', 'Téléprospection', 'Site WEB', 'Apporteur'];

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#14b8a6','#f59e0b','#10b981','#3b82f6','#ef4444','#f97316','#06b6d4'];

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    TableModule, ButtonModule, InputTextModule,
    DialogModule, DropdownModule, TagModule,
    ToastModule, ProgressSpinnerModule, TooltipModule, ConfirmDialogModule,
    CalendarModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './cas-contacts.component.html',
  styleUrls: ['./cas-contacts.component.scss'],
})
export class CasContactsComponent implements OnInit {
  private readonly filtersKey = 'contactsFiltersState';
  contacts: Contact[] = [];
  totalCount = 0;
  loading   = false;
  openingForm = false;

  // ---------- filtres ----------
  search       = '';
  filterStatut : string | null = null;
  filterType   : string | null = null;
  filterCycle  : string | null = null;
  showFilters  = true;

  // filtres avancés (serveur)
  filterOrigine      : string | null = null;
  filterEmailValidity: 'valid' | 'invalid' | null = null;
  filterUserId       : number | null = null;
  dateRange          : Date[] | null = null;
  origineFilterOptions = origineEnumOptions;
  emailValidityOptions = [
    { label: 'Email valide', value: 'valid' },
    { label: 'Email non valide', value: 'invalid' },
  ];
  usersFilter : { label: string; value: number }[] = [];
  isAdmin     = false;

  // actions de masse
  selectedAction       : string | null = null;
  selectedAssignUserId : number | null = null;
  exportingExcel       = false;

  statutLeadFilterOptions  = [{ label: 'Tous statuts', value: null as string | null }, ...STATUT_LEAD_OPTIONS.map(v => ({ label: v, value: v }))];
  typeContactFilterOptions  = [{ label: 'Tous types',   value: null as string | null }, ...TYPE_CONTACT_OPTIONS.map(v => ({ label: v, value: v }))];
  cycleDeVieFilterOptions   = [{ label: 'Tous cycles',  value: null as string | null }, ...CYCLE_DE_VIE_OPTIONS.map(v => ({ label: v, value: v }))];

  // ---------- édition inline ----------
  cycleDeVieOptions  = CYCLE_DE_VIE_OPTIONS;
  statutLeadOptions  = STATUT_LEAD_OPTIONS;
  typeContactOptions = TYPE_CONTACT_OPTIONS;
  etatCivilOptions   = ETAT_CIVIL_OPTIONS;

  // ---------- sélection ----------
  selected: Contact[] = [];

  // ---------- détails (fiche contact + ses transactions) ----------
  detailVisible       = false;
  selectedContact     : Contact | null = null;
  contactTransactions : Transaction[] = [];
  loadingTransactions = false;

  // ---------- SMS rapide ----------
  smsContent  = '';
  sendingSms  = false;

  // ---------- SMS relecture (dialog avant envoi) ----------
  smsDialogVisible = false;
  smsDialogContact : Contact | null = null;
  smsDialogPhone   = '';
  smsDialogContent = '';
  smsDialogSending = false;

  // ---------- historique SMS Ringover ----------
  smsHistory        : any[] = [];
  loadingSmsHistory = false;
  smsHistoryError   = false;

  // ---------- sélection de la transaction à ouvrir ----------
  formSelectVisible      = false;
  formSelectContact      : Contact | null = null;
  formSelectTransactions : Transaction[] = [];

  // ---------- création ----------
  createVisible = false;
  saving        = false;
  newContact    : Partial<Contact> = {};
  civiliteOptions              = ['M.', 'Mme', 'Monsieur', 'Madame'];
  typePersonneOptions          = ['Personne physique', 'Personne morale'];
  origine_du_leadOptions       = ORIGINE_LEAD_OPTIONS;
  professionalCategoryOptions  = professionalCategories;
  ouiNonOptions                = [{ label: 'Non', value: false }, { label: 'Oui', value: true }];

  constructor(
    private CasContactService: CasContactService,
    private CasTransactionService: CasTransactionService,
    private projectHistoryService: ProjectHistoryService,
    private ringoverService: RingoverService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.restoreFilters();
    this.userService.getUsers().subscribe({
      next: (users) => {
        this.usersFilter = (users || [])
          .filter(u => u.is_active !== false)
          .map(u => ({ label: u.username || u.email, value: u.id }));
      },
      error: () => {},
    });
    this.CasContactService.count().subscribe({ next: (r) => this.totalCount = r.count, error: () => {} });
    this.load();
  }

  private restoreFilters(): void {
    try {
      const raw = sessionStorage.getItem(this.filtersKey);
      if (!raw) return;
      const s = JSON.parse(raw);
      this.search             = s.search || '';
      this.filterStatut       = s.filterStatut ?? null;
      this.filterType         = s.filterType ?? null;
      this.filterCycle        = s.filterCycle ?? null;
      this.filterOrigine      = s.filterOrigine ?? null;
      this.filterEmailValidity = s.filterEmailValidity ?? null;
      this.filterUserId       = s.filterUserId ?? null;
      if (Array.isArray(s.dateRange) && s.dateRange.length === 2 && s.dateRange[0] && s.dateRange[1]) {
        this.dateRange = [new Date(s.dateRange[0]), new Date(s.dateRange[1])];
      }
    } catch { sessionStorage.removeItem(this.filtersKey); }
  }

  private persistFilters(): void {
    sessionStorage.setItem(this.filtersKey, JSON.stringify({
      search: this.search,
      filterStatut: this.filterStatut,
      filterType: this.filterType,
      filterCycle: this.filterCycle,
      filterOrigine: this.filterOrigine,
      filterEmailValidity: this.filterEmailValidity,
      filterUserId: this.filterUserId,
      dateRange: this.dateRange && this.dateRange.length === 2
        ? [this.dateRange[0]?.toISOString(), this.dateRange[1]?.toISOString()] : null,
    }));
  }

  // ===== création =====

  openCreate(): void {
    this.newContact = { fumeur: false, est_assure2: false, assure2_fumeur: false };
    this.createVisible = true;
  }

  fillExampleData(): void {
    this.newContact = {
      type_personne: 'Physique',
      civilite: 'Monsieur',
      nom: 'TESTEUR',
      prenom: 'Jean',
      date_naissance: '1980-05-15',
      etat_civil: 'Marié',
      mobile: '+33612345678',
      email: 'jean.test@example.com',
      adresse: '123 Avenue des Champs',
      ville: 'Paris',
      code_postal: '75008',
      pays: 'France',
      origine_du_lead: 'Site WEB',
      statut_du_lead: 'Nouveau',
      type_contact: 'Prospect',
      cycle_de_vie: 'Lead',
      statut_socioprofessionnel: 'Cadre supérieur',
      profession_exacte: 'Ingénieur',
      entreprise: 'Tech Corp',
      est_assure2: true,
      assure2_civilite: 'Madame',
      assure2_nom: 'TESTEUR',
      assure2_prenom: 'Marie',
      assure2_date_naissance: '1982-10-20',
      assure2_etat_civil: 'Marié',
      assure2_mobile: '+33687654321',
      assure2_email: 'marie.test@example.com',
      fumeur: false,
      assure2_fumeur: false
    };
  }

  saveContact(): void {
    if (!this.newContact.nom?.trim() && !this.newContact.prenom?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Requis', detail: 'Saisissez au moins un nom ou prénom.' });
      return;
    }
    this.saving = true;
    
    // Attribuer le contact au conseiller connecté par défaut
    if (!(this.newContact as any).user_id) {
      const currentUserId = localStorage.getItem('user_id');
      if (currentUserId) {
        (this.newContact as any).user_id = Number(currentUserId);
      }
    }

    this.CasContactService.create(this.newContact).subscribe({
      next: (c) => {
        this.contacts.unshift(c);
        this.saving = false;
        this.createVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Contact créé', detail: this.fullName(c) });
      },
      error: (err) => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de créer le contact.' });
        console.error(err);
      },
    });
  }

  // ===== données =====

  private buildContactFilters() {
    const f: any = {};
    if (this.filterOrigine)       f.origine = this.filterOrigine;
    if (this.filterEmailValidity) f.email_validity = this.filterEmailValidity;
    if (this.filterUserId != null) f.user_id = this.filterUserId;
    if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
      const s = new Date(this.dateRange[0]); s.setHours(0, 0, 0, 0);
      const e = new Date(this.dateRange[1]); e.setHours(23, 59, 59, 999);
      f.start_date = s.toISOString();
      f.end_date   = e.toISOString();
    }
    return f;
  }

  load(): void {
    this.loading = true;
    this.persistFilters();
    this.CasContactService.list(500, 0, this.search?.trim() || undefined, this.buildContactFilters()).subscribe({
      next: (data) => {
        this.contacts = data || [];
        this.loading = false;
        this.syncMissingEmailValidation();
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les contacts' });
        console.error(err);
      },
    });
  }

  /** Valide en arrière-plan les emails non encore vérifiés des contacts affichés. */
  private syncMissingEmailValidation(): void {
    const ids = this.contacts
      .filter(c => c.email && (c.email_is_valid === null || c.email_is_valid === undefined))
      .map(c => c.id);
    if (!ids.length) return;
    this.CasContactService.syncEmailValidation(ids, true).subscribe({
      next: (r) => {
        if (r.updated_count > 0) {
          // Réinjecte les résultats dans la liste sans recharger.
          const map = new Map(r.items.map((i: any) => [i.contact_id, i.email_is_valid]));
          this.contacts.forEach(c => { if (map.has(c.id)) c.email_is_valid = map.get(c.id); });
        }
      },
      error: () => {},
    });
  }

  onSearch(): void { this.load(); }

  // ===== filtres côté client (statut/type/cycle) =====

  get filteredContacts(): Contact[] {
    return this.contacts.filter(c =>
      (!this.filterStatut || c.statut_du_lead === this.filterStatut) &&
      (!this.filterType   || c.type_contact   === this.filterType)   &&
      (!this.filterCycle  || c.cycle_de_vie   === this.filterCycle)
    );
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterStatut || this.filterType || this.filterCycle || this.search?.trim()
      || this.filterOrigine || this.filterEmailValidity || this.filterUserId != null
      || (this.dateRange && this.dateRange.length === 2));
  }

  clearFilters(): void {
    this.filterStatut = null;
    this.filterType   = null;
    this.filterCycle  = null;
    this.search       = '';
    this.filterOrigine = null;
    this.filterEmailValidity = null;
    this.filterUserId = null;
    this.dateRange = null;
    this.load();
  }

  // ===== Export Excel =====

  exportExcel(): void {
    if (this.exportingExcel) return;
    const ids = this.selected.map(c => c.id);
    const useSelection = ids.length > 0;
    this.exportingExcel = true;
    const options = useSelection ? {} : { export_all_matching: true, ...this.buildContactFilters(), search: this.search?.trim() || undefined };
    this.CasContactService.exportExcel(useSelection ? ids : [], options).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) { this.exportingExcel = false; return; }
        const disposition = response.headers.get('content-disposition');
        const match = disposition ? /filename="?([^"]+)"?/.exec(disposition) : null;
        const filename = match?.[1] || `contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = filename;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.messageService.add({ severity: 'success', summary: 'Export terminé', detail: useSelection ? `${ids.length} contact(s) exporté(s).` : 'Contacts filtrés exportés.' });
        this.exportingExcel = false;
      },
      error: (err) => {
        this.exportingExcel = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Export Excel impossible.' });
        console.error(err);
      },
    });
  }

  // ===== Attribution groupée (admin) =====

  assignBulk(): void {
    if (!this.isAdmin || !this.selectedAssignUserId || !this.selected.length) {
      this.messageService.add({ severity: 'warn', summary: 'Action incomplète', detail: 'Sélectionnez des contacts et un conseiller.' });
      return;
    }
    const target = this.selectedAssignUserId;
    const label = this.usersFilter.find(u => u.value === target)?.label || 'le conseiller';
    this.confirmationService.confirm({
      message: `Attribuer ${this.selected.length} contact(s) à ${label} ?`,
      header: 'Confirmer l\'attribution', icon: 'pi pi-users',
      acceptLabel: 'Confirmer', rejectLabel: 'Annuler',
      accept: () => {
        forkJoin(this.selected.map(c => this.CasContactService.update(c.id, { user_id: target } as any))).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Attribués', detail: `${this.selected.length} contact(s) attribué(s).` });
            this.selectedAction = null; this.selectedAssignUserId = null; this.selected = [];
            this.load();
          },
          error: (err) => { this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Attribution impossible.' }); console.error(err); },
        });
      },
    });
  }

  // ===== Menu d'actions unifié =====

  bulkStatut : string | null = null;
  bulkBusy   = false;

  get actionOptions(): { label: string; value: string; icon: string }[] {
    const opts: { label: string; value: string; icon: string; admin?: boolean }[] = [
      { label: 'Exporter en Excel',            value: 'export',        icon: 'pi pi-file-excel' },
      { label: 'Changer le statut lead',       value: 'statut',        icon: 'pi pi-flag' },
      { label: 'Email demande d\'information', value: 'email_info',    icon: 'pi pi-envelope' },
      { label: 'Email économies',              value: 'email_savings', icon: 'pi pi-percentage' },
      { label: 'SMS rappel devis',             value: 'sms_rappel',    icon: 'pi pi-comment' },
      { label: 'Attribuer à un conseiller',    value: 'assign',        icon: 'pi pi-users', admin: true },
      { label: 'Supprimer la sélection',       value: 'delete',        icon: 'pi pi-trash' },
    ];
    return opts.filter(o => !o.admin || this.isAdmin);
  }

  actionNeedsSelection(value: string | null): boolean {
    return value !== null && value !== 'export';
  }

  canExecuteAction(): boolean {
    if (!this.selectedAction || this.bulkBusy || this.exportingExcel) return false;
    if (this.actionNeedsSelection(this.selectedAction) && !this.selected.length) return false;
    if (this.selectedAction === 'statut' && !this.bulkStatut) return false;
    if (this.selectedAction === 'assign' && !this.selectedAssignUserId) return false;
    return true;
  }

  executeAction(): void {
    switch (this.selectedAction) {
      case 'export':        this.exportExcel(); break;
      case 'statut':        this.applyBulkStatut(); break;
      case 'email_info':    this.sendBulkEmail(false); break;
      case 'email_savings': this.sendBulkEmail(true); break;
      case 'sms_rappel':    this.sendBulkSmsRappel(); break;
      case 'assign':        this.assignBulk(); break;
      case 'delete':        this.deleteSelected(); break;
    }
  }

  private applyBulkStatut(): void {
    if (!this.bulkStatut || !this.selected.length) return;
    const statut = this.bulkStatut;
    this.bulkBusy = true;
    forkJoin(this.selected.map(c => this.CasContactService.update(c.id, { statut_du_lead: statut } as any))).subscribe({
      next: () => {
        this.selected.forEach(c => (c.statut_du_lead = statut));
        this.bulkBusy = false;
        this.messageService.add({ severity: 'success', summary: 'Statut mis à jour', detail: `${this.selected.length} contact(s)` });
      },
      error: (err) => { this.bulkBusy = false; this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Changement de statut impossible.' }); console.error(err); },
    });
  }

  /** Résout les project_id des transactions des contacts sélectionnés puis envoie l'email step-one. */
  private sendBulkEmail(savings: boolean): void {
    if (!this.selected.length) return;
    const withEmail = this.selected.filter(c => (c.email || '').trim());
    if (!withEmail.length) { this.messageService.add({ severity: 'warn', summary: 'Aucun email', detail: 'Aucun contact sélectionné n\'a d\'email.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer l'email ${savings ? 'd\'économies' : 'de demande d\'information'} à ${withEmail.length} contact(s) ?`,
      header: 'Confirmer l\'envoi groupé', icon: 'pi pi-envelope',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        this.bulkBusy = true;
        // Récupère la transaction la plus récente de chaque contact → project_id
        forkJoin(withEmail.map(c => this.CasTransactionService.list(1, 0, { contact_id: c.id }))).subscribe({
          next: (lists) => {
            const projectIds = lists
              .map(l => (l && l.length ? l[0].project_id : null))
              .filter((p): p is string => !!p);
            if (!projectIds.length) {
              this.bulkBusy = false;
              this.messageService.add({ severity: 'warn', summary: 'Aucune transaction', detail: 'Les contacts sélectionnés n\'ont pas de dossier CAC lié.' });
              return;
            }
            this.bulkBusy = false;
            this.messageService.add({ severity: 'warn', summary: 'Non disponible', detail: 'Bulk email indisponible en Santé', life: 8000 });
          },
          error: (err) => { this.bulkBusy = false; this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de résoudre les dossiers.' }); console.error(err); },
        });
      },
    });
  }

  private contactPhoneE164(c: Contact): string | null {
    const raw = (c.mobile || '').trim();
    if (!raw) return null;
    const d = raw.replace(/\D/g, '');
    if (!d || d.length < 8) return null;
    if (d.startsWith('00')) return `+${d.slice(2)}`;
    if (d.startsWith('33')) return `+${d}`;
    if (d.startsWith('0'))  return `+33${d.slice(1)}`;
    return `+${d}`;
  }

  private formatCiviliteForSms(civilite: string | undefined): string {
    const raw = (civilite || '').trim();
    const normalized = raw.toLowerCase().replace(/\./g, '');
    if (['mme', 'madame', 'mlle', 'mademoiselle'].includes(normalized)) return 'Mme';
    if (['m', 'mr', 'monsieur'].includes(normalized)) return 'M.';
    return raw;
  }

  private sendBulkSmsRappel(): void {
    const withPhone = this.selected.filter(c => this.contactPhoneE164(c));
    const without = this.selected.length - withPhone.length;
    if (!withPhone.length) { this.messageService.add({ severity: 'warn', summary: 'Aucun numéro', detail: 'Aucun contact sélectionné n\'a de mobile.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer un SMS "Rappel Devis" à ${withPhone.length} contact(s) ?` + (without > 0 ? `\n(${without} ignoré(s) — sans mobile)` : ''),
      header: 'Confirmer l\'envoi SMS', icon: 'pi pi-comment',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        this.bulkBusy = true;
        let sent = 0, failed = 0; const total = withPhone.length;
        withPhone.forEach(c => {
          const phone = this.contactPhoneE164(c)!;
          const civ = c.civilite || '';
          const greeting = ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ.trim().toLowerCase().replace(/\./g, '')) ? 'Chère' : 'Cher';
          const smsCivilite = this.formatCiviliteForSms(civ);
          const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${c.nom ? ' ' + c.nom : ''}`;
          const content = `${salutation},\n\nVous nous avez sollicités pour des devis en assurance de crédit. Nous vous avions envoyé plusieurs devis.\nVotre besoin est-il toujours d'actualité ?\n\nBien à vous\n\nCabinet BROKINS\nwww.brokins.fr`;
          this.ringoverService.sendSms({ to_number: phone, content }).subscribe({
            next: () => { sent++; if (sent + failed === total) this.onBulkSmsDone(sent, failed); },
            error: () => { failed++; if (sent + failed === total) this.onBulkSmsDone(sent, failed); },
          });
        });
      },
    });
  }

  private onBulkSmsDone(sent: number, failed: number): void {
    this.bulkBusy = false;
    this.messageService.add({
      severity: sent > 0 ? 'success' : 'error',
      summary: sent > 0 ? 'SMS envoyés' : 'Échec SMS',
      detail: sent > 0 ? `${sent} SMS envoyé(s)${failed > 0 ? ` (${failed} échec(s))` : ''}.` : `Aucun SMS envoyé (${failed} erreur(s)).`,
      life: 8000,
    });
  }

  // ===== actions ligne =====

  get devisTransactions(): Transaction[] {
    return this.contactTransactions.filter(t =>
      t.devis_envoye === true  ||
      (t.nbre_devis_envoyes != null && Number(t.nbre_devis_envoyes) > 0) ||
      !!t.date_envoi_devis   ||
      !!t.numero_devis       ||
      !!t.nom_assureurs      ||
      !!t.assureur           ||
      !!t.numero_contrat
    );
  }

  sendQuickSms(c: Contact): void {
    const msg = this.smsContent.trim();
    if (!msg) {
      this.messageService.add({ severity: 'warn', summary: 'Vide', detail: 'Rédigez un message avant d\'envoyer.' });
      return;
    }
    const number = (c.mobile || '').replace(/\s/g, '');
    if (!number) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de mobile', detail: 'Ce contact n\'a pas de numéro mobile.' });
      return;
    }
    this.sendingSms = true;
    this.ringoverService.sendSms({ to_number: number, content: msg }).subscribe({
      next: () => {
        this.sendingSms  = false;
        this.smsContent  = '';
        this.messageService.add({ severity: 'success', summary: 'SMS envoyé', detail: `À ${number}`, life: 2500 });
      },
      error: (err) => {
        this.sendingSms = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur SMS', detail: 'Impossible d\'envoyer le SMS via Ringover.' });
        console.error(err);
      },
    });
  }

  /** Ouvre la fiche détaillée du contact, charge ses transactions et l'historique SMS. */
  openDetails(c: Contact): void {
    this.selectedContact     = c;
    this.detailVisible       = true;
    this.contactTransactions = [];
    this.smsHistory          = [];
    this.smsHistoryError     = false;
    this.loadingTransactions = true;

    this.CasTransactionService.list(500, 0, { contact_id: c.id }).subscribe({
      next: (data) => { this.contactTransactions = data || []; this.loadingTransactions = false; },
      error: (err) => {
        this.loadingTransactions = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les transactions du contact.' });
        console.error(err);
      },
    });

    const phone = (c.mobile || c.fixe || '').replace(/\s/g, '');
    if (phone) {
      this.loadingSmsHistory = true;
      this.ringoverService.getSmsHistory(phone, 50).subscribe({
        next: (data) => {
          this.smsHistory        = data?.messages || [];
          this.loadingSmsHistory = false;
        },
        error: () => {
          this.loadingSmsHistory = false;
          this.smsHistoryError   = true;
        },
      });
    }
  }

  viewTransactions(c: Contact): void {
    this.router.navigate(['/cac/transactions'], { queryParams: { contactId: c.id } });
  }

  /**
   * Bouton « Ouvrir le formulaire » : récupère les transactions du contact.
   * 0 → avertissement, 1 → ouverture directe, plusieurs → sélection de la transaction.
   */
  openInForm(c: Contact): void {
    this.openingForm = true;
    this.CasTransactionService.list(500, 0, { contact_id: c.id }).subscribe({
      next: (transactions) => {
        this.openingForm = false;
        const list = transactions || [];
        if (!list.length) {
          this.messageService.add({ severity: 'warn', summary: 'Aucune transaction', detail: "Ce contact n'a pas encore de transaction liée." });
          return;
        }
        if (list.length === 1) {
          this.loadTransactionForm(list[0]);
          return;
        }
        // Plusieurs transactions → laisser l'utilisateur choisir.
        this.formSelectContact      = c;
        this.formSelectTransactions = list;
        this.formSelectVisible      = true;
      },
      error: () => {
        this.openingForm = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les transactions.' });
      },
    });
  }

  /** Charge le dossier CAC d'une transaction donnée et ouvre le formulaire pré-rempli. */
  loadTransactionForm(t: Transaction): void {
    // Chargement direct depuis la Transaction (sans dépendre de project_history).
    this.openingForm       = true;
    this.formSelectVisible = false;
    this.CasTransactionService.getFormPayload(t.id).subscribe({
      next: (details) => {
        localStorage.setItem('projectToLoad', JSON.stringify(details));
        this.openingForm = false;
        this.router.navigate(['/cac/comparaison'], { queryParams: { loadProject: details.project_id || t.project_id } });
      },
      error: () => {
        this.openingForm = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Impossible d'ouvrir le formulaire." });
      },
    });
  }

  callPhone(phone: string | null | undefined): void {
    const number = (phone || '').replace(/\s/g, '');
    if (!number) return;
    window.location.href = `tel:${number}`;
  }

  callContact(c: Contact): void {
    const number = (c.mobile || c.fixe || '').replace(/\s/g, '');
    if (!number) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de numéro', detail: "Ce contact n'a pas de numéro de téléphone." });
      return;
    }
    // Ouvre Ringover avec le numéro (protocole tel:).
    window.location.href = `tel:${number}`;
    // En parallèle : arme le suivi serveur → passage auto en NRP si non-réponse.
    this.ringoverService.trackCall({ to_number: number, contact_id: c.id }).subscribe({
      next: (res) => {
        if (res?.calls_read) {
          this.scheduleNrpRefresh(c.id);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Suivi NRP désactivé',
            detail: res?.message || "La clé Ringover n'a pas la permission 'Calls Read'.",
            life: 8000,
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Suivi NRP indisponible',
          detail: err?.error?.detail || 'Le suivi automatique du statut n\'a pas pu être armé.',
          life: 6000,
        });
      },
    });
  }

  /** Rafraîchit le statut du contact pour refléter un éventuel passage auto en NRP.
   *  Le suivi serveur couvre ~30s + 10×25s (≈ 4,7 min) : on sonde à 120s (cas
   *  rapide) puis à 300s (cas où l'appel apparaît tard dans /calls). */
  private scheduleNrpRefresh(contactId: number): void {
    const refresh = (notify: boolean) => {
      this.CasContactService.get(contactId).subscribe({
        next: (fresh) => {
          const already = this.contacts.find(x => x.id === contactId)?.statut_du_lead;
          const becameNrp = already !== 'NRP' && fresh.statut_du_lead === 'NRP';
          const row = this.contacts.find(x => x.id === contactId);
          if (row) row.statut_du_lead = fresh.statut_du_lead;
          const sel = this.selectedContact;
          if (sel && sel.id === contactId) {
            sel.statut_du_lead = fresh.statut_du_lead;
          }
          if (notify && becameNrp) {
            this.messageService.add({
              severity: 'info',
              summary: 'Statut mis à jour',
              detail: 'Appel sans réponse → contact passé en NRP.',
            });
          }
        },
        error: () => {},
      });
    };
    setTimeout(() => refresh(true), 120000);
    setTimeout(() => refresh(true), 300000);
  }

  /** Construit le message SMS "Rappel Devis" pré-rédigé pour un contact. */
  private buildSmsRappel(c: Contact): string {
    const civ = c.civilite || '';
    const greeting = ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ.trim().toLowerCase().replace(/\./g, '')) ? 'Chère' : 'Cher';
    const smsCivilite = this.formatCiviliteForSms(civ);
    const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${c.nom ? ' ' + c.nom : ''}`;
    return `${salutation},\n\nVous nous avez sollicités pour des devis en assurance de crédit. Nous vous avions envoyé plusieurs devis.\nVotre besoin est-il toujours d'actualité ?\n\nBien à vous\n\nCabinet BROKINS\nwww.brokins.fr`;
  }

  /** Ouvre le dialog SMS pré-rédigé pour relecture avant envoi (via API Ringover). */
  smsContact(c: Contact): void {
    const phone = this.contactPhoneE164(c);
    if (!phone) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de numéro', detail: "Ce contact n'a pas de mobile renseigné." });
      return;
    }
    this.smsDialogContact = c;
    this.smsDialogPhone   = phone;
    this.smsDialogContent = this.buildSmsRappel(c);
    this.smsDialogVisible = true;
  }

  /** Envoi effectif du SMS depuis le dialog (après relecture). */
  sendSmsFromDialog(): void {
    if (!this.smsDialogContact || !this.smsDialogPhone || !this.smsDialogContent.trim()) return;
    this.smsDialogSending = true;
    this.ringoverService.sendSms({ to_number: this.smsDialogPhone, content: this.smsDialogContent }).subscribe({
      next: () => {
        this.smsDialogSending = false;
        this.smsDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'SMS envoyé', detail: `À ${this.smsDialogPhone}`, life: 4000 });
      },
      error: (err) => {
        this.smsDialogSending = false;
        this.messageService.add({ severity: 'error', summary: 'Échec SMS', detail: err?.error?.detail || 'Impossible d\'envoyer le SMS via Ringover.' });
        console.error(err);
      },
    });
  }

  deleteOne(c: Contact): void {
    this.confirmationService.confirm({
      message: `Supprimer le contact ${this.fullName(c)} et toutes ses transactions ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.CasContactService.delete(c.id).subscribe({
          next: () => {
            this.contacts = this.contacts.filter(x => x.id !== c.id);
            this.selected = this.selected.filter(x => x.id !== c.id);
            this.messageService.add({ severity: 'success', summary: 'Supprimé', detail: this.fullName(c) });
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Suppression impossible' });
            console.error(err);
          },
        });
      },
    });
  }

  deleteSelected(): void {
    if (!this.selected.length) return;
    this.confirmationService.confirm({
      message: `Supprimer ${this.selected.length} contact(s) et leurs transactions ?`,
      header: 'Confirmation de suppression groupée',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        forkJoin(this.selected.map(c => this.CasContactService.delete(c.id))).subscribe({
          next: () => {
            const ids = new Set(this.selected.map(c => c.id));
            this.contacts = this.contacts.filter(x => !ids.has(x.id));
            this.selected = [];
            this.messageService.add({ severity: 'success', summary: 'Supprimés', detail: `${ids.size} contact(s) supprimé(s)` });
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Suppression groupée impossible' });
            console.error(err);
            this.load();
          },
        });
      },
    });
  }

  updateField(c: Contact, field: keyof Contact, value: any): void {
    this.CasContactService.update(c.id, { [field]: value } as Partial<Contact>).subscribe({
      next: () => {
        (c as any)[field] = value;
        this.messageService.add({ severity: 'success', summary: 'Mis à jour', detail: `${field} modifié`, life: 1500 });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Mise à jour impossible' });
        console.error(err);
        this.load();
      },
    });
  }

  // ===== helpers =====

  fullName(c: Contact): string {
    return [c.prenom, c.nom].filter(Boolean).join(' ') || '—';
  }

  ownerLabel(c: Contact): string {
    if (c.user?.username || c.user?.email) {
      return c.user.username || c.user.email || '—';
    }
    const targetId = (c as any).user_id || (c as any).userId;
    if (targetId) {
      const found = this.usersFilter.find(u => u.value === targetId);
      if (found) return found.label;

      const currentUserId = localStorage.getItem('user_id');
      if (currentUserId && Number(currentUserId) === Number(targetId)) {
        return localStorage.getItem('username') || localStorage.getItem('email') || 'Moi';
      }
    }
    return '—';
  }

  initials(c: Contact): string {
    const f = (c.prenom || '').charAt(0);
    const l = (c.nom || '').charAt(0);
    return (f + l).toUpperCase() || '?';
  }

  avatarColor(c: Contact): string {
    return AVATAR_COLORS[(c.id || 0) % AVATAR_COLORS.length];
  }

  age(dateStr?: string): number | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
    return a >= 0 && a < 130 ? a : null;
  }

  location(c: Contact): string {
    return [c.code_postal, c.ville].filter(Boolean).join(' ') || '';
  }

  statutSeverity(statut?: string): any {
    switch (statut) {
      case 'Converti en transaction': return 'success';
      case 'En cours - Devis Envoyé':
      case 'En cours - QS Envoyé':    return 'info';
      case 'A recontacter':           return 'warning';
      case 'NRP':
      case 'Non qualifié - Refus':
      case 'Perdu - Trop Cher':
      case 'Perdu - Refus Assureur':  return 'danger';
      default:                        return 'secondary';
    }
  }

  hasDevis(t: Transaction): boolean {
    return t.devis_envoye === true || Number(t.nbre_devis_envoyes) > 0 || !!t.nom_assureurs;
  }

  parseDevisHistory(t: Transaction): any[] {
    if (!t.nom_assureurs) return [];
    try {
      const parsed = JSON.parse(t.nom_assureurs);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return [];
  }

  devisHistoryOrFallback(t: Transaction): { isJson: boolean; items: any[]; raw: string } {
    const items = this.parseDevisHistory(t);
    return {
      isJson : items.length > 0,
      items,
      raw    : items.length ? '' : (t.nom_assureurs || ''),
    };
  }

  phaseSeverity(phase?: string): any {
    switch (phase) {
      case 'Fermé gagné':                   return 'success';
      case 'Fermé perdu':                   return 'danger';
      case 'Devis envoyé':
      case 'Questionnaire médical envoyé':  return 'info';
      case 'Relance & objections en cours': return 'warning';
      default:                              return 'secondary';
    }
  }
}
